/**
 * Parejas de letra para Font, en Combinations.
 *
 * Antes la lista salía de las combinaciones de color, que casi siempre ponían
 * Inter de cuerpo: veinte parejas que se leían igual en el texto corrido. Aquí
 * cada una tiene su letra de párrafo, pensada para ella:
 *
 * - Marcas de una sola familia: la misma en títulos y cuerpo, como hacen las
 *   empresas con tipografía propia; el peso separa una cosa de otra.
 * - Títulos con carácter —serif, display, grotesca— y una sans de lectura
 *   tranquila que no compite con ellos.
 * - Serif en los dos, para lo editorial.
 *
 * Todas son del catálogo del editor (`GOOGLE_FONTS`): lo que se elige aquí se
 * puede volver a elegir a mano en el selector de fuente.
 */
import type { ProjectTheme } from './types'
import { applyFontsToTheme } from './presets'

export type FontPair = {
  id: string
  heading: string
  body: string
  /** Qué lleva serif, para la pila de reserva y el filtro. */
  serif?: 'heading' | 'both'
  /**
   * El título es una cursiva de caligrafía: va sólo en los títulos grandes
   * (H1 y H2) y a peso normal. Los títulos chicos, el antetítulo, las
   * insignias y las cifras llevan la letra del cuerpo: en cursiva, el nombre
   * de una tarjeta o un antetítulo en mayúsculas no se leían.
   */
  script?: boolean
}

const pair = (heading: string, body: string, serif?: FontPair['serif'], script = false): FontPair => ({
  id: `${heading}|${body}`.toLowerCase().replace(/[^a-z0-9|]+/g, '-'),
  heading,
  body,
  ...(serif ? { serif } : {}),
  ...(script ? { script: true } : {}),
})
/** Una pareja de cursiva: la cursiva en los títulos grandes; `bodySerif`, si el cuerpo es serif. */
const scriptPair = (heading: string, body: string, bodySerif = false): FontPair => pair(heading, body, bodySerif ? 'both' : undefined, true)

export const FONT_PAIRS: FontPair[] = [
  // Una sola familia
  pair('Inter', 'Inter'),
  pair('Manrope', 'Manrope'),
  pair('Plus Jakarta Sans', 'Plus Jakarta Sans'),
  pair('DM Sans', 'DM Sans'),
  pair('Figtree', 'Figtree'),
  pair('Work Sans', 'Work Sans'),
  pair('IBM Plex Sans', 'IBM Plex Sans'),
  pair('Outfit', 'Outfit'),
  pair('Archivo', 'Archivo'),
  pair('Public Sans', 'Public Sans'),
  // Títulos con carácter y cuerpo de lectura
  pair('Space Grotesk', 'DM Sans'),
  pair('Sora', 'IBM Plex Sans'),
  pair('Urbanist', 'Mulish'),
  pair('Epilogue', 'Karla'),
  pair('Lexend', 'Public Sans'),
  pair('Red Hat Display', 'Work Sans'),
  pair('Oswald', 'Barlow'),
  pair('JetBrains Mono', 'IBM Plex Sans'),
  // Friendly / graphic: títulos expresivos con cuerpos de lectura tranquila.
  pair('Shrikhand', 'DM Sans', 'heading'),
  pair('Chicle', 'Manrope'),
  pair('Spicy Rice', 'Work Sans', 'heading'),
  pair('Modak', 'DM Sans'),
  pair('Lilita One', 'Nunito'),
  pair('Paytone One', 'Public Sans'),
  pair('Dela Gothic One', 'IBM Plex Sans'),
  pair('Bagel Fat One', 'Figtree'),
  pair('Coiny', 'Mulish'),
  pair('Bowlby One SC', 'Barlow'),
  // Serif en los títulos
  pair('Instrument Serif', 'Manrope', 'heading'),
  pair('Playfair Display', 'DM Sans', 'heading'),
  pair('Fraunces', 'Figtree', 'heading'),
  pair('Lora', 'DM Sans', 'heading'),
  pair('Libre Baskerville', 'Public Sans', 'heading'),
  pair('Cormorant Garamond', 'Manrope', 'heading'),
  pair('Source Serif 4', 'IBM Plex Sans', 'heading'),
  pair('EB Garamond', 'Work Sans', 'heading'),
  pair('Spectral', 'Karla', 'heading'),
  // Serif en los dos
  pair('Instrument Serif', 'Source Serif 4', 'both'),
  pair('Lora', 'Lora', 'both'),
  // Lujo: las mismas parejas de los diez presets, disponibles sin su paleta.
  pair('Bodoni Moda', 'Tenor Sans', 'heading'),
  pair('Gothic A1', 'Gothic A1'),
  pair('Italiana', 'Hanken Grotesk'),
  pair('Marcellus', 'Tenor Sans', 'heading'),
  pair('DM Serif Display', 'Hanken Grotesk', 'heading'),
  pair('Tenor Sans', 'Tenor Sans'),
  pair('Prata', 'Hanken Grotesk', 'heading'),
  pair('Questrial', 'Hanken Grotesk'),
  pair('Hanken Grotesk', 'Hanken Grotesk'),
  pair('Libre Caslon Display', 'Hanken Grotesk', 'heading'),
  // Cursivas elegantes en los títulos grandes, para bodas, belleza, eventos…
  scriptPair('Great Vibes', 'Montserrat'),
  scriptPair('Parisienne', 'Lora', true),
  scriptPair('Allura', 'Raleway'),
  /* Raleway y no Cormorant: a tamaño de párrafo Cormorant se leía muy chica junto a la cursiva. */
  scriptPair('Pinyon Script', 'Raleway'),
  scriptPair('Italianno', 'Josefin Sans'),
  scriptPair('Petit Formal Script', 'Work Sans'),
]

export function fontStack(name: string, serif: boolean, script = false) {
  return `'${name}', ${script ? 'cursive' : serif ? 'serif' : 'sans-serif'}`
}

/** La pila de reserva de los títulos de una pareja. */
export function headingStack(item: Pick<FontPair, 'heading' | 'serif' | 'script'>) {
  return fontStack(item.heading, Boolean(item.serif), Boolean(item.script))
}

export function applyFontPairToTheme(theme: ProjectTheme, item: FontPair): ProjectTheme {
  return applyFontsToTheme(
    theme,
    headingStack(item),
    fontStack(item.body, item.serif === 'both'),
    item.id,
    item.script ? { displayOnly: true } : undefined,
  ) as ProjectTheme
}

/** La primera familia de una pila CSS: `'Lora', serif` → `Lora`. */
function familyOf(stack: unknown) {
  return typeof stack === 'string' ? stack.split(',')[0]!.trim().replace(/^['"]|['"]$/g, '') : ''
}

/**
 * La que está puesta, mirando las letras de verdad y no el id guardado: un
 * título cambiado a mano en el selector de fuente ya no es esa pareja.
 */
export function activeFontPairId(theme: ProjectTheme): string | null {
  const heading = familyOf(theme.roles.heading1.fontFamily)
  const body = familyOf(theme.roles.paragraph.fontFamily)
  return FONT_PAIRS.find((item) => item.heading === heading && item.body === body)?.id ?? null
}
